'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/stores/auth-store'
import { useDynamicFontLoader } from '@/components/dynamic-font-loader'

export interface ColorScheme {
  name: string
  primary: string
  secondary: string
  accent: string
  background?: string
  gradient?: string
}

export interface ThemePreferences {
  fontFamily: string
  fontSize: number
  colorScheme: ColorScheme
  lightThemeColor?: string
  darkThemeColor?: string
  theme?: 'light' | 'dark' | 'system'
}

const defaultColorScheme: ColorScheme = {
  name: 'Oxigen Purple',
  primary: 'hsl(240, 41%, 24%)',
  secondary: 'hsl(240, 32%, 31%)',
  accent: 'hsl(240, 41%, 24%)'
}

const defaultPreferences: ThemePreferences = {
  fontFamily: 'Inter',
  fontSize: 16,
  colorScheme: defaultColorScheme
}

export function useThemePreferences() {
  const [preferences, setPreferences] = useState<ThemePreferences>(defaultPreferences)
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasLoadedFromFirebase, setHasLoadedFromFirebase] = useState(false)
  const { user } = useAuth()

  // Dynamically load the selected font on demand
  useDynamicFontLoader(preferences.fontFamily)

  // Listen for font changes from language switches
  useEffect(() => {
    const handleStorageChange = () => {
      const fontFamily = localStorage.getItem('fontFamily')
      if (fontFamily && fontFamily !== preferences.fontFamily) {
        setPreferences(prev => {
          // Only update if actually different
          if (prev.fontFamily !== fontFamily) {
            return { ...prev, fontFamily }
          }
          return prev
        })
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--font-family', fontFamily)
        }
      }
    }

    const handleFontChanged = (event: Event) => {
      const customEvent = event as CustomEvent
      const fontFamily = customEvent.detail?.fontFamily
      if (fontFamily && fontFamily !== preferences.fontFamily) {
        setPreferences(prev => {
          // Only update if actually different
          if (prev.fontFamily !== fontFamily) {
            return { ...prev, fontFamily }
          }
          return prev
        })
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--font-family', fontFamily)
        }
      }
    }

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange)
    
    // Listen for custom fontChanged events
    window.addEventListener('fontChanged', handleFontChanged)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('fontChanged', handleFontChanged)
    }
  }, []) // Remove preferences.fontFamily dependency to prevent loops

  // Load preferences from Firebase first, then localStorage as fallback
  useEffect(() => {
    // Prevent re-loading if already loaded
    if (isLoaded && hasLoadedFromFirebase) return

    const loadPreferences = async () => {
      // First, always check localStorage (which may have been synced by useSyncPreferences)
      const stored = localStorage.getItem('oxigen-theme-preferences')
      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          setPreferences({ ...defaultPreferences, ...parsed })
          // Remove verbose localStorage log
        } catch (e) {
          console.error('Failed to parse theme preferences:', e)
        }
      }

      try {
        // Then try to load from Firebase if user is authenticated
        if (user?.id && !hasLoadedFromFirebase) {
          const response = await fetch(`/api/user-preferences?userId=${user.id}`)
          if (response.ok) {
            const data = await response.json()
            if (data.prefs) {
              // Build preferences from the same structure as EOD reminders
              const loadedPrefs: Partial<ThemePreferences> = {}
              
              if (data.prefs.fontFamily) loadedPrefs.fontFamily = data.prefs.fontFamily
              if (data.prefs.fontSize) loadedPrefs.fontSize = data.prefs.fontSize
              if (data.prefs.colorScheme) loadedPrefs.colorScheme = data.prefs.colorScheme
              if (data.prefs.theme) loadedPrefs.theme = data.prefs.theme
              
              if (Object.keys(loadedPrefs).length > 0) {
                // Merge with current state (from localStorage) instead of defaults
                setPreferences(prev => ({ ...prev, ...loadedPrefs }))
                // Remove verbose Firebase log
              }
            }
          }
          setHasLoadedFromFirebase(true)
        }
      } catch (error) {
        console.error('Failed to load theme preferences from Firebase:', error)
      }

      setIsLoaded(true)
    }

    loadPreferences()
  }, [user?.id, isLoaded, hasLoadedFromFirebase])

  // Apply preferences to document
  useEffect(() => {
    if (!isLoaded) return

    const root = document.documentElement

    // Mark that we have a custom theme active
    root.setAttribute('data-custom-theme', 'true')

    // Apply font family
    root.style.setProperty('--font-family', preferences.fontFamily)

    // Apply font size
    root.style.setProperty('--font-size-base', `${preferences.fontSize}px`)

    // Apply color scheme
    if (preferences.colorScheme.primary) {
      // Handle both hsl() format and raw HSL values
      let primaryHSL = preferences.colorScheme.primary

      if (primaryHSL.startsWith('hsl(')) {
        primaryHSL = primaryHSL.replace('hsl(', '').replace(')', '')
      }
      // CRITICAL: Remove commas to convert from "234, 78%, 24%" to "234 78% 24%"
      // CSS custom properties need space-separated values to work with hsl(var(--primary))
      primaryHSL = primaryHSL.replace(/,/g, '').trim()

      root.style.setProperty('--primary', primaryHSL)

      // For dark mode, extract and use a lighter version
      const match = primaryHSL.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/)
      if (match) {
        const [, h, s, l] = match
        root.style.setProperty('--primary-dark', `${h} ${s}% ${Math.min(parseInt(l) + 10, 65)}%`)
      }
    }
    if (preferences.colorScheme.secondary) {
      let secondaryHSL = preferences.colorScheme.secondary
      if (secondaryHSL.startsWith('hsl(')) {
        secondaryHSL = secondaryHSL.replace('hsl(', '').replace(')', '')
      }
      // Remove commas and normalize to space-separated format
      secondaryHSL = secondaryHSL.replace(/,/g, '').trim()
      root.style.setProperty('--secondary', secondaryHSL)
    }
    if (preferences.colorScheme.accent) {
      let accentHSL = preferences.colorScheme.accent
      if (accentHSL.startsWith('hsl(')) {
        accentHSL = accentHSL.replace('hsl(', '').replace(')', '')
      }
      // Remove commas and normalize to space-separated format
      accentHSL = accentHSL.replace(/,/g, '').trim()
      root.style.setProperty('--accent', accentHSL)
      root.style.setProperty('--accent-custom', accentHSL)
          }

    // Set button background (solid color only, no gradients)
    root.style.setProperty('--button-hover-bg', preferences.colorScheme.primary)
    root.classList.remove('gradient-background')

    // Update favicon color to match theme
    updateFavicon(preferences.colorScheme.primary)
  }, [preferences, isLoaded])

  // Function to update favicon with theme color
  const updateFavicon = (color: string) => {
    if (typeof document === 'undefined') return
    try {
      // Convert HSL to hex for SVG
      const hslMatch = color.match(/hsl\((\d+),?\s*(\d+)%?,?\s*(\d+)%?\)/)
      let rgb = { r: 94, g: 61, b: 155 } // Default purple
      if (hslMatch) {
        const [, h, s, l] = hslMatch.map(Number)
        rgb = hslToRgb(h, s / 100, l / 100)
      }
      const hexColor = `#${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}`
      
      // Create SVG favicon matching ox-one.svg logo structure with theme color
      const svg = `
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0)">
            <path d="M24.012 0C31.2887 2.60878e-05 38.5671 1.96786 42.3509 5.92578C49.9018 13.5194 49.8965 34.7026 42.218 42.2168C38.4315 46.0733 31.2169 48 24.0032 48C16.7896 47.9999 9.57324 46.0714 5.78543 42.2178C-1.92316 34.6418 -1.92272 13.3605 5.78738 5.78613C9.58354 1.93528 16.797 0 24.012 0ZM24.012 4.25098C17.4868 4.25098 11.521 6.02595 8.81375 8.77148L8.79031 8.7959L8.7659 8.81934C6.02715 11.5101 4.25515 17.4697 4.25515 23.999C4.25523 30.5324 6.02584 36.4941 8.7659 39.1875L8.81765 39.2393C11.5097 41.9793 17.4713 43.7499 24.0032 43.75C30.5353 43.75 36.4951 41.9794 39.1858 39.2393L39.2151 39.209L39.2454 39.1787C41.9334 36.5469 43.7005 30.6881 43.7444 24.252C43.7882 17.6775 42.0584 11.6611 39.3362 8.92383L39.3059 8.89355L39.2776 8.86328C36.5992 6.06147 30.6071 4.251 24.012 4.25098ZM15.7024 15.6436C20.3145 15.6436 24.054 19.384 24.054 23.9961C24.0539 28.6081 20.3145 32.3477 15.7024 32.3477C11.0904 32.3476 7.35097 28.6081 7.35086 23.9961C7.35086 19.384 11.0903 15.6436 15.7024 15.6436ZM40.5667 19.1406L35.7112 23.9961L40.5667 28.8506L37.1653 32.252L32.3108 27.3975L27.4553 32.252L24.054 28.8506L28.9095 23.9961L24.054 19.1406L27.4553 15.7402L32.3108 20.5947L37.1653 15.7402L40.5667 19.1406ZM15.7024 20.1943C13.6028 20.1944 11.9007 21.8964 11.9007 23.9961C11.9008 26.0956 13.6028 27.7978 15.7024 27.7979C17.802 27.7979 19.5041 26.0957 19.5042 23.9961C19.5042 21.8964 17.8021 20.1943 15.7024 20.1943Z" fill="${hexColor}"/>
          </g>
          <defs>
            <clipPath id="clip0">
              <rect width="48" height="48" fill="white"/>
            </clipPath>
          </defs>
        </svg>
      `.trim()
      
      // Convert SVG to data URL
      const svgBlob = new Blob([svg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(svgBlob)
      
      // Always replace favicon link
      let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
      }
      link.type = 'image/svg+xml'
      link.href = url
      // Remove any existing favicon links
      const head = document.getElementsByTagName('head')[0]
      const links = head.querySelectorAll("link[rel*='icon']")
      links.forEach(l => {
        if (l !== link) head.removeChild(l)
      })
      if (!link.parentNode) {
        head.appendChild(link)
      }
    } catch (error) {
      console.error('Failed to update favicon:', error)
    }
  }

  // Helper function to convert HSL to RGB
  const hslToRgb = (h: number, s: number, l: number) => {
    let r, g, b

    if (s === 0) {
      r = g = b = l
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1/6) return p + (q - p) * 6 * t
        if (t < 1/2) return q
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
        return p
      }

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hue2rgb(p, q, h / 360 + 1/3)
      g = hue2rgb(p, q, h / 360)
      b = hue2rgb(p, q, h / 360 - 1/3)
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    }
  }

  const updatePreferences = (updates: Partial<ThemePreferences>) => {
    const newPreferences = { ...preferences, ...updates }
    setPreferences(newPreferences)
    
    // Save to localStorage for immediate access
    localStorage.setItem('oxigen-theme-preferences', JSON.stringify(newPreferences))
    
    // Save to Firebase if user is authenticated - use same structure as EOD reminders
    if (user?.id) {
      fetch('/api/user-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          preferences: {
            fontFamily: newPreferences.fontFamily,
            fontSize: newPreferences.fontSize,
            colorScheme: newPreferences.colorScheme,
            theme: newPreferences.theme
          }
        })
      }).catch(error => {
        console.error('Failed to save theme preferences to Firebase:', error)
      })
    }
  }

  const updateColorScheme = (scheme: ColorScheme) => {
    updatePreferences({ colorScheme: scheme })
  }

  const updateFontFamily = (fontFamily: string) => {
    updatePreferences({ fontFamily })

    // Also update the separate fontFamily key in localStorage for language hook compatibility
    localStorage.setItem('fontFamily', fontFamily)

    // Force immediate CSS update
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--font-family', fontFamily)
    }
  }

  const updateFontSize = (fontSize: number) => {
    updatePreferences({ fontSize })
  }

  const resetToDefaults = () => {
    setPreferences(defaultPreferences)
    localStorage.removeItem('oxigen-theme-preferences')
  }

  return {
    preferences,
    updatePreferences,
    updateColorScheme,
    updateFontFamily,
    updateFontSize,
    resetToDefaults,
    isLoaded
  }
}
